// src/i18n/index.ts
import { createI18n, type I18nOptions } from 'vue-i18n';
import methods from '@/utils/localMethod';
import { liveRoomBaseUrl } from '@/api/serviceUrls';
import { normalizeUrlBase } from '@/utils/urlBase';

export const LANGUAGE_KEY = 'lang-local';

// 可用语言列表（选择器展示顺序：英文、韩语、中文）
export const languages: Array<{ code: string; name: string; flag: string }> = [
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'ko', name: '한글', flag: 'KR' },
  { code: 'zh', name: '简体中文', flag: 'CN' },
];

// 获取浏览器语言
const getBrowserLocale = (): string => {
  const browserLang = navigator.language || 'zh';
  const lang = browserLang.split('-')[0];
  const t = languages.find((item) => item.code.startsWith(lang));
  if (t) {
    return t.code;
  }
  return 'zh'; // 默认语言
};

// 从 localStorage 获取保存的语言设置
const getSavedLocale = (): string | null => {
  const saved = localStorage.getItem(LANGUAGE_KEY);
  if (saved && languages.some((l) => l.code === saved)) {
    return saved;
  }
  return null;
};

// 创建 i18n 实例
let i18n: any = null;
// const i18n = createI18n({
//     legacy: false, // 使用 Composition API
//     locale: getSavedLocale() || getBrowserLocale(),
//     fallbackLocale: 'zh',
//     messages: {}, // 改为空对象，不再直接导入
//     globalInjection: true,
// });

export const getI18nInstance = (): any => {
  return i18n;
};

/** 与界面语言一致：同步 html lang + 韩语用 class（避免仅依赖 lang 属性匹配失败） */
export function syncDocumentLocaleFromCode(locale: string): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const primary =
    String(locale || 'zh')
      .trim()
      .split('-')[0]
      .toLowerCase() || 'zh';
  root.setAttribute('lang', primary);
  root.classList.toggle('aimeta-locale-ko', primary === 'ko');
}

// 兼容桌面端语言包占位符：{{var}} -> {var}（vue-i18n）
const normalizePlaceholders = (input: any): any => {
  if (typeof input === 'string') {
    return input.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, '{$1}');
  }
  if (Array.isArray(input)) {
    return input.map((item) => normalizePlaceholders(item));
  }
  if (input && typeof input === 'object') {
    const result: Record<string, any> = {};
    Object.keys(input).forEach((key) => {
      result[key] = normalizePlaceholders(input[key]);
    });
    return result;
  }
  return input;
};

export type LoadLanguageOptions = { force?: boolean };

/** 与 request.ts / config ClientUrl 一致，避免相对路径落到 127.0.0.1 */
function collectClientWebOrigins(): string[] {
  const origins: string[] = [];
  const push = (raw?: string) => {
    const base = normalizeUrlBase(String(raw || ''));
    if (base && !origins.includes(base)) origins.push(base);
  };
  push(liveRoomBaseUrl);
  push(import.meta.env.VITE_LOCAL_PATH as string | undefined);
  if (typeof window !== 'undefined') {
    push(window.location.origin);
  }
  return origins;
}

function languagePackUrl(lang: string, origin: string): string {
  return `${origin.replace(/\/+$/, '')}/Language/${lang}.json`;
}

async function fetchWebLanguagePack(
  lang: string
): Promise<Record<string, any> | null> {
  for (const origin of collectClientWebOrigins()) {
    try {
      const url = languagePackUrl(lang, origin);
      const response = await fetch(url);
      if (!response.ok) continue;
      const messages = await response.json();
      if (messages && Object.keys(messages).length > 0) {
        return normalizePlaceholders(messages) as Record<string, any>;
      }
    } catch {
      // 尝试下一个候选地址
    }
  }
  return null;
}

function applyLocaleMessages(lang: string, messages: Record<string, any>): void {
  i18n.global.setLocaleMessage(lang, messages);
  setLocaleSync(lang);
  dispatchI18nUpdated(lang);
}

// 加载语言包（force：始终从宿主重新拉取并覆盖缓存，便于多 WebView 与磁盘语言包热更新）
export async function loadLanguageAsync(
  lang: string,
  options?: LoadLanguageOptions
): Promise<void> {
  const force = options?.force === true;
  if (!force && i18n.global.availableLocales.includes(lang)) {
    setLocaleSync(lang);
    dispatchI18nUpdated(lang);
    return;
  }

  const webMessages = await fetchWebLanguagePack(lang);

  // 统一走 public/Language（与浏览器一致）：Windows 壳、Android WebView、浏览器均不再合并宿主磁盘语言包，
  // 避免内置旧包覆盖 dev/部署后的新文案。
  if (webMessages) {
    applyLocaleMessages(lang, webMessages);
    return;
  }

  console.log(`No language pack available for: ${lang}`);
  applyLocaleMessages(lang, {});
}

function dispatchI18nUpdated(lang: string) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent('aimeta:i18n-updated', { detail: { lang } })
  );
}

// 类型安全的切换语言函数（修改，支持异步）
export const setLocale = async (
  locale: string,
  forceReload?: boolean
): Promise<void> => {
  if (forceReload) {
    await loadLanguageAsync(locale, { force: true });
    return;
  }
  if (!i18n.global.availableLocales.includes(locale)) {
    await loadLanguageAsync(locale);
  } else {
    (i18n.global.locale as any).value = locale;
    localStorage.setItem(LANGUAGE_KEY, locale);
    syncDocumentLocaleFromCode(locale);
    dispatchI18nUpdated(locale);
  }
};

// 同步切换语言的简化版（用于已加载的语言）
export const setLocaleSync = (locale: string): void => {
  if (i18n.global.availableLocales.includes(locale)) {
    (i18n.global.locale as any).value = locale;
    localStorage.setItem(LANGUAGE_KEY, locale);
    syncDocumentLocaleFromCode(locale);
  }
};

// 获取当前语言
export const getCurrentLocale = (): string => {
  return i18n.global.locale.value;
};

// 获取当前语言信息
export const getCurrentLanguage = () => {
  const currentLocale = getCurrentLocale();
  return languages.find((lang) => lang.code === currentLocale) || languages[0];
};

// 预加载默认语言（新增：在应用启动时加载默认语言）
// const defaultLocale = getSavedLocale() || getBrowserLocale();
// loadLanguageAsync(defaultLocale);

// export default i18n;

export async function createI18nInstance(lang?: string) {
  let defaultLocale = getSavedLocale() || lang || getBrowserLocale();
  i18n = createI18n({
    legacy: false, // 使用 Composition API
    locale: defaultLocale,
    fallbackLocale: 'zh',
    messages: {}, // 改为空对象，不再直接导入
    globalInjection: true,
  });
  // 尝试加载背地语言
  try {
    const apiLocale = await methods.getLanguage();
    if (apiLocale && isValidLocale(apiLocale)) {
      defaultLocale = normalizeLocaleCode(apiLocale);
    }
  } catch (error) {
    console.warn('获取接口语言失败，使用默认语言:', error);
    // 失败时使用初始值，不做处理
  }
  (i18n.global.locale as any).value = defaultLocale;
  syncDocumentLocaleFromCode(defaultLocale);
  // 启动时强制拉取 Web 语言包，避免脚本更新后仍沿用旧缓存文案。
  await loadLanguageAsync(defaultLocale, { force: true });
  return i18n;
}

// 验证语言是否有效（兼容宿主返回 ko-KR、zh-CN 等）
const isValidLocale = (locale: string): boolean => {
  const primary = String(locale || '')
    .trim()
    .split('-')[0]
    .toLowerCase();
  return languages.some((l) => l.code === primary);
};

const normalizeLocaleCode = (locale: string): string => {
  const primary = String(locale || '')
    .trim()
    .split('-')[0]
    .toLowerCase();
  const hit = languages.find((l) => l.code === primary);
  return hit ? hit.code : primary;
};
